import { PageContainer, Text, VStack } from '@/components/Atoms'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import {
  resolveComparison,
  useCostTypeVariance,
  type CostTypeVariance,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import {
  DEFAULT_VARIANCE_MEASURE,
  getVarianceCostTypeRowUnit,
  VARIANCE_GROUPING_DIMENSION_TYPES,
} from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { CRT_ROUTES, toStaticData } from '../crt-route-config'
import { DrillDataTable } from './-components/DrillDataTable'
import { DrillPageHeader } from './-components/DrillPageHeader'
import { DrillRowActions, type DrillRowAction } from './-components/DrillRowActions'
import {
  drillRoutesFor,
  TARGET_LABEL_KEYS,
  VARIANCE_ROUTE_BY_DIMENSION,
  type DrillRow,
} from './-data/drill-dimensions'
import { costTypeLabelKey, sourceLabelKey } from './-data/drill-labels'
import {
  buildRowDrillSearch,
  drillSearchSchema,
  requireComparisonSides,
  toCostTypeScope,
  toDrillFilters,
} from './-data/drill-search'

export const Route = createFileRoute('/_auth/_modules/crt/variance-analysis/by-cost-type')({
  staticData: toStaticData(CRT_ROUTES['/crt/variance-analysis/by-cost-type']),
  component: RouteComponent,
  validateSearch: (search) => drillSearchSchema.parse(search),
  beforeLoad: ({ search }) => requireComparisonSides(search),
})

/**
 * The endpoint's rows as the drill table reads them.
 *
 * Two names differ, and they differ on purpose rather than by oversight. The endpoint speaks the KPI
 * tree's vocabulary (`kpiKey`, `breakdown`); the four drill pages share one flat dimension vocabulary
 * whose names double as URL params (`costType`, `subRows`). Mapping at the boundary keeps each side
 * idiomatic, and keeps `buildRowDrillSearch` and the applied-filter badges working off the dimension
 * names they already use.
 */
function toCostTypeDrillRows(data: CostTypeVariance | undefined): DrillRow[] {
  if (!data) return []

  return data.results.map((row) => ({
    grouping: { costType: row.grouping.kpiKey },
    variance: row.variance,
    subRows: row.breakdown.map((entry) => ({
      grouping: { costType: entry.grouping.kpiKey, source: entry.grouping.source },
      variance: entry.variance,
    })),
  }))
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  // `state: true` keeps the drill history (`drillFrom`) on the entry, as the header's own navigations do.

  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true, state: true }),
  })

  // The comparison and the cell both come out of the search: the header configured the two sides, and
  // the heat-map cell the reader clicked to get here named the material scope.
  const comparison = resolveComparison(search)
  const scope = toCostTypeScope(search)

  // Which ladder this page is showing. AR reports the cash ladder instead of the investment grid, and
  // the two share nothing but the cell they describe.
  const measure = search.measure ?? DEFAULT_VARIANCE_MEASURE
  const isArLadder = measure === 'ar'

  const { data, isPending, isPlaceholderData, error } = useCostTypeVariance(
    comparison,
    scope,
    measure,
  )
  const rows = toCostTypeDrillRows(data)

  /** Why the table has nothing to show, when it has nothing to show. */
  const unavailableError = !comparison
    ? new Error(t('varianceAnalysis.emptyState'))
    : !scope
      ? new Error(t('varianceAnalysis.drill.byCostType.unavailableScope'))
      : null

  /**
   * Everything a row leads to, which on this page is two different kinds of thing.
   *
   * The drill hangs off the **sub-row**, where the source is known. A parent row spans several sources
   * that lead to different pages, so there is no single reading to send it to. The AR ladder has no
   * sources at all, so it never reaches that branch — and the measure is handed to the rule anyway, so
   * that what the ladder offers is stated rather than left to follow from the shape of its rows.
   *
   * The measure switch hangs off `nto` because that is the one row of the AR ladder the other ladder is
   * built from: leaving AR there lands on the same figure, broken into the spend that makes it up.
   */
  const targetsForRow = (row: DrillRow): DrillRowAction[] => {
    const targets: DrillRowAction[] = []

    if (row.grouping.source && row.grouping.costType) {
      const targetSearch = buildRowDrillSearch(search, row, ['costType', 'source'])

      if (targetSearch) {
        targets.push(
          ...drillRoutesFor({ ...toDrillFilters(targetSearch), measure }).map((to) => ({
            to,
            search: targetSearch,
            label: t(TARGET_LABEL_KEYS[to]),
          })),
        )
      }
    }

    if (isArLadder && row.grouping.costType === 'nto') {
      targets.push({
        to: VARIANCE_ROUTE_BY_DIMENSION.byCostType,
        search: { ...search, measure: 'nto' },
        label: t('varianceAnalysis.drill.actions.byNtoBreakdown'),
      })
    }

    return targets
  }

  const kpiColumn: ColumnDef<DrillRow, unknown> = {
    id: 'costType',
    accessorFn: (row) => row.grouping.costType ?? undefined,
    sortUndefined: 'last',
    header: () =>
      isArLadder
        ? t('varianceAnalysis.drill.table.kpi')
        : t('varianceAnalysis.drill.table.costType'),
    // Only the parent names the cost type: repeating it down its own sub-rows would read as five
    // separate cost types rather than one broken into its sources.
    cell: ({ row }) =>
      row.depth > 0 ? null : (
        <Text weight="medium">{t(costTypeLabelKey(String(row.original.grouping.costType)))}</Text>
      ),
  }

  const sourceColumn: ColumnDef<DrillRow, unknown> = {
    id: 'source',
    accessorFn: (row) => row.grouping.source ?? undefined,
    sortUndefined: 'last',
    header: () => t('varianceAnalysis.drill.table.source'),
    cell: ({ row }) => {
      const source = row.original.grouping.source
      if (!source) return null
      return <Text tone="muted">{t(sourceLabelKey(source))}</Text>
    },
  }

  // No source column on the AR ladder: not one of its rows is spend, so none has a source to name and
  // the column would be empty from top to bottom.
  const leadingColumns: ColumnDef<DrillRow, unknown>[] = isArLadder
    ? [kpiColumn]
    : [kpiColumn, sourceColumn]

  return (
    <PageContainer maxWidth="wide">
      <VStack gap="lg" align="stretch">
        <DrillPageHeader
          title={
            isArLadder
              ? t('varianceAnalysis.drill.byCostType.arPageHeader')
              : t('varianceAnalysis.drill.byCostType.pageHeader')
          }
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byCostType}
          hasMeasureSwitch
        />

        <DrillDataTable
          search={search}
          dimension={VARIANCE_GROUPING_DIMENSION_TYPES.byCostType}
          rows={rows}
          leadingColumns={leadingColumns}
          getSubRows={(row) => row.subRows}
          renderRowActions={(row) => (
            <DrillRowActions
              from={VARIANCE_GROUPING_DIMENSION_TYPES.byCostType}
              targets={targetsForRow(row.original)}
            />
          )}
          currency={conversion}
          rowUnit={(row) => getVarianceCostTypeRowUnit(row.grouping.costType)}
          isPending={isPending}
          isRefreshing={isPlaceholderData}
          error={error ?? unavailableError}
          // A cost type is spend, so more of it is money going out: the increase reads as unfavourable,
          // the opposite of the heat map's NTO. The start price is revenue, as is the AR ladder, and both
          // read the other way.
          isIncreaseFavourable={(row) => isArLadder || row.grouping.costType === 'startPrice'}
        />
      </VStack>
    </PageContainer>
  )
}
